// Animation helpers for the Buddha Game scene.
// This file contains functions for idle floating animation, aura pulsing,
// rising/falling movement for gameplay, and win animation sequences.

import type { Group, Mesh } from 'three';

export interface AnimationContext {
  // The Buddha character group for applying position animations
  characterGroup?: Group | null;
  // The aura mesh for pulsing/fading effects
  auraMesh?: Mesh | null;
  // The breathing ring mesh for brightness pulses
  ringMesh?: Mesh | null;
  // heart model removed — no longer used
  // Base Y position of the character (to float around)
  baseY?: number;
  // Accumulated elapsed time for smooth animations
  elapsed?: number;
}

// Track the last provided context so one-shot feedback helpers can find meshes
let latestContext: AnimationContext | null = null;

function updateLatestContext(ctx?: AnimationContext): void {
  if (!ctx) return;
  latestContext = {
    ...latestContext,
    characterGroup:
      ctx.characterGroup !== undefined
        ? ctx.characterGroup
        : (latestContext?.characterGroup ?? null),
    auraMesh:
      ctx.auraMesh !== undefined
        ? ctx.auraMesh
        : (latestContext?.auraMesh ?? null),
    ringMesh:
      ctx.ringMesh !== undefined
        ? ctx.ringMesh
        : (latestContext?.ringMesh ?? null),
    // heartModel removed — not tracked
    baseY: ctx.baseY !== undefined ? ctx.baseY : latestContext?.baseY,
    elapsed: ctx.elapsed !== undefined ? ctx.elapsed : latestContext?.elapsed,
  };
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ============================================================================
// MOVEMENT STATE
// ============================================================================
// Tracks the current movement mode and position for rising/falling animations.

export type MovementMode = 'idle' | 'rising' | 'falling' | 'win';

interface MovementState {
  mode: MovementMode;
  // The current Y offset from the base position (for rising/falling)
  currentOffset: number;
  // Whether movement system has been initialized
  initialized: boolean;
  // Progress-based offset (separate from movement offset)
  progressOffset: number;
}

const movementState: MovementState = {
  mode: 'idle',
  currentOffset: 0,
  initialized: false,
  progressOffset: 0,
};

// ============================================================================
// MOVEMENT CONFIGURATION
// ============================================================================
// These values control how the Buddha rises and falls.
// They can be adjusted later for gameplay tuning.

const RISE_SPEED = 0.8; // Units per second when rising (slow and calm)
const FALL_SPEED = 1.0; // Units per second when falling (slightly faster)
const MIN_OFFSET = 0; // Cannot sink below starting position
const MAX_OFFSET = 3.5; // Maximum height above starting position

// ============================================================================
// IDLE FLOATING ANIMATION
// ============================================================================
// A very subtle, slow up/down floating motion that makes the Buddha feel alive.
// The movement is extremely slow (like a calm breath) and only moves a few
// centimeters in world space. No side-to-side wobble, just smooth vertical motion.

// Configuration for the idle floating animation
const IDLE_FLOAT_AMPLITUDE = 0.03; // How far to float up/down (in world units) - very subtle
const IDLE_FLOAT_SPEED = 0.4; // How fast to complete one cycle (lower = slower)

// Internal state for tracking the base position
let baseYInitialized = false;
let storedBaseY = 0;

// Debug movement overrides (used by the debug UI)
const debugMovement = {
  // When true the breathing-movement is frozen and the debug UI controls position
  frozen: false,
  // When enabled manualY will be applied directly to the character group position
  manualControlEnabled: false,
  // Manual Y value (world-space). If null, no manual value is applied.
  manualY: null as number | null,
};

// ============================================================================
// FEEDBACK EFFECT STATES (ring/aura/character shake)
// ============================================================================

interface PulseEffectState {
  active: boolean;
  startMs: number;
  durationMs: number;
  from: number;
  to: number;
}

interface ShakeEffectState {
  active: boolean;
  startMs: number;
  durationMs: number;
  magnitudeRad: number;
  baseRotationZ: number;
}

const ringEffect: PulseEffectState = {
  active: false,
  startMs: 0,
  durationMs: 0,
  from: 1,
  to: 1,
};

const auraEffect: PulseEffectState = {
  active: false,
  startMs: 0,
  durationMs: 0,
  from: 1,
  to: 1,
};

const shakeEffect: ShakeEffectState = {
  active: false,
  startMs: 0,
  durationMs: 0,
  magnitudeRad: 0,
  baseRotationZ: 0,
};

function startPulseEffect(
  target: PulseEffectState,
  to: number,
  durationMs: number,
  from: number = 1
): void {
  target.active = true;
  target.startMs = nowMs();
  target.durationMs = durationMs;
  target.from = from;
  target.to = to;
}

function getPulseValue(effect: PulseEffectState, timeMs: number): number {
  if (!effect.active) return effect.to;

  const progress = clamp((timeMs - effect.startMs) / effect.durationMs, 0, 1);
  const pulse = Math.sin(progress * Math.PI); // Smooth in/out curve
  const value = effect.from + pulse * (effect.to - effect.from);

  if (progress >= 1) {
    effect.active = false;
    return effect.from;
  }

  return value;
}

function applyRingEffect(mesh: Mesh | null, timeMs: number): void {
  if (!mesh || !ringEffect.active) return;
  const material = mesh.material as any;
  if (!material || typeof material.opacity !== 'number') return;

  const targetOpacity = getPulseValue(ringEffect, timeMs);
  material.opacity = targetOpacity;
}

function applyAuraEffectMultiplier(timeMs: number): number {
  if (!auraEffect.active) return 1;
  return getPulseValue(auraEffect, timeMs);
}

function applyShakeEffect(group: Group | null, timeMs: number): void {
  if (!group || !shakeEffect.active) return;

  const progress = clamp(
    (timeMs - shakeEffect.startMs) / shakeEffect.durationMs,
    0,
    1
  );
  const decay = 1 - progress;
  const wobble =
    Math.sin(progress * Math.PI * 4) * shakeEffect.magnitudeRad * decay;

  group.rotation.z = shakeEffect.baseRotationZ + wobble;

  if (progress >= 1) {
    group.rotation.z = shakeEffect.baseRotationZ;
    shakeEffect.active = false;
  }
}

// ============================================================================
// FEEDBACK TRIGGERS
// ============================================================================

export function pulseRing(
  ctx?: AnimationContext,
  durationMs: number = 260
): void {
  updateLatestContext(ctx);
  const mesh = ctx?.ringMesh ?? latestContext?.ringMesh ?? null;
  if (!mesh) return;

  const material = mesh.material as any;
  const baseOpacity =
    typeof material?.opacity === 'number' ? material.opacity : 1;

  startPulseEffect(
    ringEffect,
    baseOpacity * 1.18,
    clamp(durationMs, 200, 350),
    baseOpacity
  );
}

export function dimRing(
  ctx?: AnimationContext,
  durationMs: number = 200
): void {
  updateLatestContext(ctx);
  const mesh = ctx?.ringMesh ?? latestContext?.ringMesh ?? null;
  if (!mesh) return;

  const material = mesh.material as any;
  const baseOpacity =
    typeof material?.opacity === 'number' ? material.opacity : 1;

  startPulseEffect(
    ringEffect,
    baseOpacity * 0.55,
    clamp(durationMs, 150, 300),
    baseOpacity
  );
}

export function pulseAura(
  ctx?: AnimationContext,
  durationMs: number = 260
): void {
  updateLatestContext(ctx);
  startPulseEffect(auraEffect, 1.22, clamp(durationMs, 200, 350), 1);
}

export function dimAura(
  ctx?: AnimationContext,
  durationMs: number = 200
): void {
  updateLatestContext(ctx);
  startPulseEffect(auraEffect, 0.7, clamp(durationMs, 150, 300), 1);
}

export function softShakeBuddha(
  ctx?: AnimationContext,
  durationMs: number = 180,
  magnitudeDegrees: number = 1.6
): void {
  updateLatestContext(ctx);
  const group = ctx?.characterGroup ?? latestContext?.characterGroup ?? null;
  if (!group) return;

  const magnitudeRad = (magnitudeDegrees * Math.PI) / 180;
  shakeEffect.active = true;
  shakeEffect.startMs = nowMs();
  shakeEffect.durationMs = clamp(durationMs, 150, 220);
  shakeEffect.magnitudeRad = clamp(magnitudeRad, 0, (2 * Math.PI) / 180); // cap at ~2 degrees
  shakeEffect.baseRotationZ = group.rotation.z;
}

export function updateIdle(ctx?: AnimationContext) {
  updateLatestContext(ctx);

  const group = ctx?.characterGroup ?? latestContext?.characterGroup ?? null;
  const elapsed = ctx?.elapsed ?? latestContext?.elapsed ?? 0;

  // Initialize the base Y position on first call
  if (group && !baseYInitialized) {
    storedBaseY = group.position.y;
    baseYInitialized = true;
    movementState.initialized = true;
  }

  if (group) {
    // If debug freeze is enabled OR manual control is enabled, apply manual position
    // rather than the regular idle + movement offsets. This allows precise tuning
    // of the buddhaGroup.position.y without fighting the breathing animations.
    if (
      debugMovement.frozen ||
      (debugMovement.manualControlEnabled &&
        typeof debugMovement.manualY === 'number')
    ) {
      // If a manual value is provided use that; otherwise fall back to stored base
      const manualY =
        typeof debugMovement.manualY === 'number'
          ? debugMovement.manualY
          : storedBaseY;
      group.position.y = manualY;
      return;
    }
    // Use provided baseY or the stored one
    const baseY = ctx?.baseY ?? storedBaseY;

    // Calculate the floating offset using a sine wave for smooth, continuous motion
    // The sine wave creates a natural, breathing-like rhythm
    const floatOffset =
      Math.sin(elapsed * IDLE_FLOAT_SPEED * Math.PI * 2) * IDLE_FLOAT_AMPLITUDE;

    // Apply the floating offset PLUS movement offset PLUS progress offset
    // This allows idle floating to continue even while rising/falling
    // progressOffset represents the permanent height gained through successful breathing
    group.position.y =
      baseY +
      floatOffset +
      movementState.currentOffset +
      movementState.progressOffset;
  }

  const timeMs = nowMs();

  // Apply gentle shake if active (failure feedback)
  applyShakeEffect(group, timeMs);

  // Apply ring pulse/dim effects even if no character group is present
  const ringMesh = ctx?.ringMesh ?? latestContext?.ringMesh ?? null;
  applyRingEffect(ringMesh, timeMs);
}

// Debug helper APIs ---------------------------------------------------------
export function enableManualPositionOverride(enabled: boolean) {
  debugMovement.manualControlEnabled = !!enabled;
  if (!enabled) debugMovement.manualY = null;
}

export function setManualPositionY(y: number | null) {
  debugMovement.manualY = y === null ? null : Number(y);
}

export function getManualPositionY(): number | null {
  return debugMovement.manualY;
}

export function setFreezeMovement(enabled: boolean) {
  debugMovement.frozen = !!enabled;
}

export function isMovementFrozen(): boolean {
  return debugMovement.frozen;
}

// Reset the idle animation state (useful when reloading the character)
export function resetIdleAnimation() {
  baseYInitialized = false;
  storedBaseY = 0;
  movementState.mode = 'idle';
  movementState.currentOffset = 0;
  movementState.progressOffset = 0;
  movementState.initialized = false;
  ringEffect.active = false;
  auraEffect.active = false;
  shakeEffect.active = false;
}

// ============================================================================
// AURA GLOW ANIMATION
// ============================================================================
// A soft, warm aura that pulses gently around the Buddha.
// Uses warm sunrise tones (whites, golds, soft orange) and fades slowly.
// The effect is subtle and peaceful, not flashy or distracting.

// Configuration for the aura pulsing animation
const AURA_PULSE_SPEED = 0.25; // How fast the aura pulses (lower = slower)
const AURA_MIN_OPACITY = 0.15; // Minimum opacity during pulse
const AURA_MAX_OPACITY = 0.35; // Maximum opacity during pulse

export function updateAura(ctx?: AnimationContext) {
  updateLatestContext(ctx);
  const mesh = ctx?.auraMesh ?? latestContext?.auraMesh;
  if (!mesh) return;

  const elapsed = ctx?.elapsed ?? 0;
  const material = mesh.material as any;

  if (!material) return;

  const timeMs = nowMs();

  // Calculate the pulsing opacity using a sine wave
  // Use a slower, offset sine wave for a more organic feel
  const pulsePhase = elapsed * AURA_PULSE_SPEED * Math.PI * 2;

  // Combine two sine waves at different frequencies for more organic pulsing
  const primaryPulse = Math.sin(pulsePhase) * 0.5 + 0.5; // 0 to 1
  const secondaryPulse = Math.sin(pulsePhase * 0.7 + 1.2) * 0.5 + 0.5; // Offset, slower

  // Blend the two pulses
  const combinedPulse = primaryPulse * 0.7 + secondaryPulse * 0.3;

  // Map to the opacity range
  const targetOpacity =
    AURA_MIN_OPACITY + combinedPulse * (AURA_MAX_OPACITY - AURA_MIN_OPACITY);

  // Apply aura feedback multipliers (win + success/failure pulses)
  const auraFeedbackMultiplier = applyAuraEffectMultiplier(timeMs);
  const totalIntensity = auraIntensityMultiplier * auraFeedbackMultiplier;
  const finalOpacity = clamp(targetOpacity * totalIntensity, 0, 1.5);

  // Apply the opacity to the material
  if (typeof material.opacity === 'number') {
    material.opacity = finalOpacity;
  }

  // Also slightly pulse the scale for an extra subtle effect
  const scaleBase = 1.0;
  const scalePulse = 1 + Math.sin(pulsePhase * 0.5) * 0.02; // Very subtle scale pulse
  const feedbackScale = 1 + (totalIntensity - 1) * 0.04; // Slightly grow when brightened
  mesh.scale.setScalar(scaleBase * scalePulse * feedbackScale);

  // Note: Aura position is now controlled by the parent buddhaGroup
  // It moves automatically with the Buddha when rising/falling
}

// ============================================================================
// RISING & FALLING ANIMATIONS
// ============================================================================
// These functions handle smooth vertical movement for gameplay.
// Rising is triggered by inhaling (later), falling by exhaling (later).
// For now, they are controlled manually for testing.

/**
 * Update the rising animation.
 * Moves the Buddha upward smoothly at a fixed speed.
 * Respects the maximum height boundary.
 */
export function updateRise(dt: number, _ctx?: AnimationContext) {
  if (movementState.mode !== 'rising') return;

  // Calculate the new offset
  const newOffset = movementState.currentOffset + RISE_SPEED * dt;

  // Clamp to maximum height
  movementState.currentOffset = Math.min(newOffset, MAX_OFFSET);

  // If we hit the ceiling, could optionally stop rising
  // For now, just clamp and continue
}

/**
 * Update the falling animation.
 * Moves the Buddha downward smoothly at a fixed speed.
 * Respects the minimum height boundary (cannot go below start).
 */
export function updateFall(dt: number, _ctx?: AnimationContext) {
  if (movementState.mode !== 'falling') return;

  // Calculate the new offset
  const newOffset = movementState.currentOffset - FALL_SPEED * dt;

  // Clamp to minimum height (starting position)
  movementState.currentOffset = Math.max(newOffset, MIN_OFFSET);

  // If we hit the floor, could optionally stop falling
  // For now, just clamp and continue
}

// ============================================================================
// MOVEMENT CONTROL FUNCTIONS
// ============================================================================
// These functions are used to start/stop rising and falling.
// They will be connected to input (mouse/touch) in later steps.

/**
 * Start rising movement.
 * The Buddha will smoothly rise until stopMovement() is called
 * or the maximum height is reached.
 */
export function startRising() {
  movementState.mode = 'rising';
  // eslint-disable-next-line no-console
  console.debug('animations: startRising()');
}

/**
 * Start falling movement.
 * The Buddha will smoothly fall until stopMovement() is called
 * or the minimum height is reached.
 */
export function startFalling() {
  movementState.mode = 'falling';
  // eslint-disable-next-line no-console
  console.debug('animations: startFalling()');
}

/**
 * Stop all movement and return to idle floating.
 */
export function stopMovement() {
  movementState.mode = 'idle';
  // eslint-disable-next-line no-console
  console.debug('animations: stopMovement()');
}

/**
 * Get the current movement state for debugging.
 */
export function getMovementState(): {
  mode: MovementMode;
  currentOffset: number;
  progressOffset: number;
  totalOffset: number;
  minOffset: number;
  maxOffset: number;
} {
  return {
    mode: movementState.mode,
    currentOffset: movementState.currentOffset,
    progressOffset: movementState.progressOffset,
    totalOffset: movementState.currentOffset + movementState.progressOffset,
    minOffset: MIN_OFFSET,
    maxOffset: MAX_OFFSET,
  };
}

/**
 * Reset the movement offset to zero (starting position).
 * Useful for restarting the game.
 */
export function resetMovement() {
  movementState.currentOffset = 0;
  movementState.progressOffset = 0;
  movementState.mode = 'idle';
  // eslint-disable-next-line no-console
  console.debug('animations: resetMovement()');
}

// ============================================================================
// PROGRESS-BASED POSITIONING
// ============================================================================
// Functions to update the Buddha's position based on breathing progress.

/**
 * Set the progress offset directly.
 * This is the permanent height gained through successful breathing cycles.
 * @param offset - The progress offset in world units
 */
export function setProgressOffset(offset: number): void {
  movementState.progressOffset = Math.max(0, offset);
}

/**
 * Get the current progress offset.
 */
export function getProgressOffset(): number {
  return movementState.progressOffset;
}

/**
 * Smoothly animate the progress offset to a target value.
 * Call this each frame during progress transitions.
 * @param targetOffset - The target progress offset
 * @param dt - Delta time in seconds
 * @param speed - Animation speed (units per second), default 1.0
 * @returns true if animation is complete
 */
export function animateProgressOffset(
  targetOffset: number,
  dt: number,
  speed: number = 1.0
): boolean {
  const diff = targetOffset - movementState.progressOffset;

  if (Math.abs(diff) < 0.01) {
    movementState.progressOffset = targetOffset;
    return true;
  }

  const step = Math.sign(diff) * speed * dt;

  // Don't overshoot
  if (Math.abs(step) > Math.abs(diff)) {
    movementState.progressOffset = targetOffset;
    return true;
  }

  movementState.progressOffset += step;
  return false;
}

// ============================================================================
// WIN ANIMATION
// ============================================================================
// Functions for the peaceful win animation sequence.

interface WinAnimationState {
  active: boolean;
  phase: 'rising' | 'settling' | 'complete';
  timer: number;
  targetY: number;
  startY: number;
}

const winAnimState: WinAnimationState = {
  active: false,
  phase: 'rising',
  timer: 0,
  targetY: 0,
  startY: 0,
};

// Win animation configuration
const WIN_ANIM_CONFIG = {
  // Rising phase: Buddha slowly rises the last distance to the cloud
  riseDuration: 2.0, // seconds
  // Settling phase: Buddha gently settles onto the cloud
  settleDuration: 1.5, // seconds
  // Extra height to rise above cloud before settling
  overshootHeight: 0.3,
};

/**
 * Start the win animation sequence.
 * @param cloudY - The Y position of the cloud (goal)
 */
export function startWinAnimation(cloudY: number): void {
  winAnimState.active = true;
  winAnimState.phase = 'rising';
  winAnimState.timer = 0;
  winAnimState.startY = movementState.progressOffset;
  winAnimState.targetY = cloudY + WIN_ANIM_CONFIG.overshootHeight;

  movementState.mode = 'win';

  // eslint-disable-next-line no-console
  console.debug('animations: win animation started');
}

/**
 * Update the win animation.
 * Should be called every frame when win animation is active.
 * @param dt - Delta time in seconds
 * @returns Object with animation state info
 */
export function updateWinAnimation(dt: number): {
  active: boolean;
  phase: string;
  progress: number;
  complete: boolean;
} {
  if (!winAnimState.active) {
    return { active: false, phase: 'none', progress: 0, complete: false };
  }

  winAnimState.timer += dt;

  if (winAnimState.phase === 'rising') {
    // Rising phase: smoothly rise to just above the cloud
    const progress = Math.min(
      winAnimState.timer / WIN_ANIM_CONFIG.riseDuration,
      1
    );

    // Use ease-out cubic for smooth deceleration
    const easedProgress = 1 - Math.pow(1 - progress, 3);

    movementState.progressOffset =
      winAnimState.startY +
      (winAnimState.targetY - winAnimState.startY) * easedProgress;

    // Transition to settling phase
    if (progress >= 1) {
      winAnimState.phase = 'settling';
      winAnimState.timer = 0;
      winAnimState.startY = movementState.progressOffset;
      // Settle to slightly below the overshoot (on the cloud)
      winAnimState.targetY =
        winAnimState.targetY - WIN_ANIM_CONFIG.overshootHeight * 0.7;
    }

    return {
      active: true,
      phase: 'rising',
      progress: easedProgress,
      complete: false,
    };
  } else if (winAnimState.phase === 'settling') {
    // Settling phase: gently settle onto the cloud
    const progress = Math.min(
      winAnimState.timer / WIN_ANIM_CONFIG.settleDuration,
      1
    );

    // Use ease-in-out for a gentle landing
    const easedProgress =
      progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

    movementState.progressOffset =
      winAnimState.startY +
      (winAnimState.targetY - winAnimState.startY) * easedProgress;

    // Animation complete
    if (progress >= 1) {
      winAnimState.phase = 'complete';
      winAnimState.active = false;
      movementState.mode = 'idle';

      // eslint-disable-next-line no-console
      console.debug('animations: win animation complete');

      return { active: false, phase: 'complete', progress: 1, complete: true };
    }

    return {
      active: true,
      phase: 'settling',
      progress: easedProgress,
      complete: false,
    };
  }

  return { active: false, phase: 'complete', progress: 1, complete: true };
}

/**
 * Check if win animation is currently active.
 */
export function isWinAnimationActive(): boolean {
  return winAnimState.active;
}

/**
 * Reset the win animation state.
 */
export function resetWinAnimation(): void {
  winAnimState.active = false;
  winAnimState.phase = 'rising';
  winAnimState.timer = 0;
  winAnimState.targetY = 0;
  winAnimState.startY = 0;
}

// ============================================================================
// AURA INTENSITY CONTROL
// ============================================================================
// Functions to control aura brightness for win effects.

let auraIntensityMultiplier = 1.0;

/**
 * Set the aura intensity multiplier.
 * Used to brighten the aura during win animation.
 * @param multiplier - Intensity multiplier (1.0 = normal, 2.0 = double brightness)
 */
export function setAuraIntensity(multiplier: number): void {
  auraIntensityMultiplier = Math.max(0, multiplier);
}

/**
 * Get the current aura intensity multiplier.
 */
export function getAuraIntensity(): number {
  return auraIntensityMultiplier;
}

/**
 * Reset aura intensity to normal.
 */
export function resetAuraIntensity(): void {
  auraIntensityMultiplier = 1.0;
}

// Heart breathing removed — visual heart is no longer part of the scene

export default {
  updateIdle,
  updateAura,
  updateRise,
  updateFall,
  resetIdleAnimation,
  // Feedback helpers
  pulseRing,
  dimRing,
  pulseAura,
  dimAura,
  softShakeBuddha,
  startRising,
  startFalling,
  stopMovement,
  getMovementState,
  resetMovement,
  // Progress-based positioning
  setProgressOffset,
  getProgressOffset,
  animateProgressOffset,
  // Debug overrides
  enableManualPositionOverride,
  setManualPositionY,
  getManualPositionY,
  setFreezeMovement,
  isMovementFrozen,
  // Win animation
  startWinAnimation,
  updateWinAnimation,
  isWinAnimationActive,
  resetWinAnimation,
  // Aura intensity
  setAuraIntensity,
  getAuraIntensity,
  resetAuraIntensity,
};
